'use client';

import { Table } from '@tanstack/react-table';

import type { XlsxColumn } from 'src/lib/xlsx/types';

import { useExport, UseExportReturn } from '../exporter';

import { tableQueryToSearch } from './helper';
import { getTableHeader } from './utils';

export interface UseDataTableExportOptions {
  /** 表格实例 */
  table: Table<any>;
  /** api endpoint */
  apiEndpoint?: string;
  // 默认的查询条件
  defaultQuery?: Record<string, any>;
  /** 自定义总行数（可选），如果不传则自动计算 */
  totalRows?: number;
}

export type UseDataTableExportReturn = UseExportReturn;

/**
 * 通用表格导出 Hook
 *
 * @example
 * ```typescript
 * const exporter = useDataTableExport({
 *   table,
 *   apiEndpoint: '/api/settings/vehicle/export', // 可选，默认使用当前路径 + /export
 * });
 *
 * <Exporter {...exporter} filename="车辆档案" />
 * ```
 */
export function useDataTableExport(options: UseDataTableExportOptions): UseDataTableExportReturn {
  const { table, apiEndpoint, defaultQuery, totalRows: customTotalRows } = options;

  // 计算总行数
  // 如果传入了自定义的 totalRows，直接使用；否则按原逻辑计算
  // 服务端分页时，使用 table 提供的行数
  // 客户端分页时，使用过滤后的所有行数（不受分页影响）
  const totalRows =
    customTotalRows ??
    (table.options.manualPagination
      ? table.getRowCount()
      : countRows(table.getFilteredRowModel().rows));

  const headers: XlsxColumn[][] = getTableHeader(table);
  const tableState = table.getState();
  const query = tableQueryToSearch({
    sorting: tableState.sorting,
    columnFilters: tableState.columnFilters,
  });

  const exporter = useExport({
    totalRows,
    headers,
    apiEndpoint,
    query: { ...defaultQuery, ...query },
  });
  return exporter;
}

function countRows(rows: any[]): number {
  return rows.reduce((count, row) => {
    // 排除合计行(isTotal 为 true 的行)
    if (row.original?.isTotal) return count;
    if (!row.subRows?.length) return count + 1;
    return count + countRows(row.subRows);
  }, 0);
}
